// **Redux Imports
import { createSlice } from '@reduxjs/toolkit'
import { ALL_CANDIDATES_ID } from '../../../utils/constant'

// constants
const tableFilterInitState = {
  query: '',
  page: 0,
  pageSize: 10
}

const LIST_FILTERS_INITIAL = {
  page: 0,
  search: '',
  size: 50,
  tenantId: '',
  isArchive: false,
  order: 'desc'
}

export const contestSlice = createSlice({
  name: 'workspace',
  initialState: {
    // contest -- general
    selectedContest: {},

    // contest -- edit
    stageTableFilters: { page: 0, pageSize: 10 },
    candidateTableFilters: {
      ...tableFilterInitState,
      page: 0,
      pageSize: 50,
      status: [],
      query: '',
      showInvitedTable: null,
      dynamicColumns: [],
      sortBy: 'email',
      sortDir: 'asc'
    },
    pendingEvaluationTableFilters: { page: 0, pageSize: 50 },
    leaderboardTableFilters: {
      ...tableFilterInitState,
      pageSize: 50,
      endDate: null,
      startDate: null,
      sortModel: [{ field: 'stages', sort: 'asc' }]
    },

    // contest -- stage level
    stageLeaderboardTableFilters: {
      page: 0,
      pageSize: 10,
      sortBy: 'score',
      sortOrder: 'desc',
      query: '',
      status: ''
    },
    stagePendingEvaluationTableFilters: { ...tableFilterInitState },

    // contest table filters -- dashboard
    contestDashboardFilters: {
      ...tableFilterInitState,
      status: ''
    },

    // assessment table filters -- dashboard
    selectedStageId: ALL_CANDIDATES_ID,
    isResumeStageSelected: false,
    assessmentDashboardTableFilters: {
      page: 0,
      size: 10,
      sortOrder: 'desc',
      sortBy: 'name',
      search: '',
      status: [],
      toDate: null,
      fromDate: null
    },

    // contest/assessment list filters
    listFilters: {
      contest: {
        ...LIST_FILTERS_INITIAL,
        sort: 'startTime'
      },
      assessment: {
        ...LIST_FILTERS_INITIAL,
        sort: 'createdTimestamp'
      }
    }
  },
  reducers: {
    // contest -- general
    setSelectedContest(state, action) {
      state.selectedContest[action.payload.key] = action?.payload?.value || {}
      if (action?.payload?.key === 'general') {
        const canCloseRegistration =
          !!action?.payload?.value?.canCloseRegistration
        const showInvitedTable = !canCloseRegistration

        if (state.candidateTableFilters.showInvitedTable === null) {
          state.candidateTableFilters = {
            ...state.candidateTableFilters,
            showInvitedTable,
            status: []
          }
        }
      }

      // contest dashboard filters
      if (action.payload.key === 'contestStepperSelectedStageId') {
        state.stageLeaderboardTableFilters.page = 0
        state.stageLeaderboardTableFilters.status = ''
        state.stageLeaderboardTableFilters.query = ''
      }
    },

    // contest -- edit reducers
    setStageTableFilters(state, action) {
      state.stageTableFilters = action.payload
    },
    setCandidateTableFilters(state, action) {
      state.candidateTableFilters = action.payload
    },
    resetCandidateTableFilters(state, action) {
      // reset candidate table filters but keep any overrides from action.payload
      state.candidateTableFilters = {
        ...tableFilterInitState,
        page: 0,
        pageSize: 50,
        status: [],
        query: '',
        showInvitedTable: null,
        dynamicColumns: [],
        sortBy: 'email',
        sortDir: 'asc',
        ...action?.payload
      }
    },

    setManualTableFilters(state, action) {
      state.pendingEvaluationTableFilters = action.payload
    },
    setLeaderboardTableFilters(state, action) {
      state.leaderboardTableFilters = action.payload
    },

    // contest -- stage level reducers
    setStageLeaderboardTableFilters(state, action) {
      const stageLeaderbaordFilters = state.stageLeaderboardTableFilters
      state.stageLeaderboardTableFilters = {
        ...stageLeaderbaordFilters,
        ...action.payload
      }
    },
    setStagePendingEvaluationTableFilters(state, action) {
      state.stagePendingEvaluationTableFilters = action.payload
    },

    // resetting stage level filters
    resetAllStageFilters(state) {
      state.stagePendingEvaluationTableFilters = { ...tableFilterInitState }
      state.stageLeaderboardTableFilters = {
        page: 0,
        pageSize: 10,
        sortBy: 'score',
        sortOrder: 'desc',
        query: '',
        status: ''
      }
    },

    // resetting contest level filters
    resetAllContestFilters(state) {
      state.selectedContest.contestStepperSelectedStageId = null
      state.contestDashboardFilters = { ...tableFilterInitState, status: '' }
      state.stageTableFilters = { page: 0, pageSize: 10 }
      state.pendingEvaluationTableFilters = { page: 0, pageSize: 50 }
      state.leaderboardTableFilters = {
        query: '',
        page: 0,
        pageSize: 50,
        endDate: null,
        startDate: null,
        sortModel: [{ field: 'stages', sort: 'asc' }]
      }
      state.candidateTableFilters = {
        query: '',
        page: 0,
        pageSize: 50,
        status: [],
        showInvitedTable: null,
        sortBy: 'email',
        sortDir: 'asc'
      }
      state.selectedStageId = ALL_CANDIDATES_ID
      state.isResumeStageSelected = false
      state.assessmentDashboardTableFilters = {
        page: 0,
        size: 10,
        sortOrder: 'desc',
        sortBy: 'lastActivityTime',
        search: '',
        status: [],
        toDate: null,
        fromDate: null
      }
    },

    // contest dashboard reducers
    setContestDashboardFilters(state, action) {
      state.contestDashboardFilters = action.payload
    },

    // assessment dashboard reducers
    setSelectedStageId(state, action) {
      state.assessmentDashboardTableFilters.search = ''
      state.assessmentDashboardTableFilters.page = 0
      state.assessmentDashboardTableFilters.status = []
      state.assessmentDashboardTableFilters.toDate = null
      state.assessmentDashboardTableFilters.fromDate = null
      state.selectedStageId = action.payload?.selectedStageId
      state.assessmentDashboardTableFilters.sortOrder = 'desc'
      if (action.payload?.selectedStageId) {
        state.assessmentDashboardTableFilters.sortBy =
          action.payload?.sortBy || 'name'
      } else {
        state.assessmentDashboardTableFilters.sortBy =
          action.payload?.sortBy || 'name'
      }
      state.isResumeStageSelected =
        action.payload?.isResumeStageSelected || false
    },
    setSearchQuery(state, action) {
      state.selectedStageId = ALL_CANDIDATES_ID
      if (state.isResumeStageSelected) {
        state.assessmentDashboardTableFilters.sortBy = 'lastActivityTime'
        state.assessmentDashboardTableFilters.sortOrder = 'desc'
      }
      state.isResumeStageSelected = false
      state.assessmentDashboardTableFilters.page = 0
      state.assessmentDashboardTableFilters.search = action.payload
    },
    setAssessmentDashboardTableFilters(state, action) {
      state.assessmentDashboardTableFilters = action.payload
    },

    // contest/assessment list filters reducers
    setListFilters(state, action) {
      const { type, filters } = action.payload
      state.listFilters[type] = filters
    },
    resetListFilters(state, action) {
      const type = action.payload
      state.listFilters[type] = {
        ...LIST_FILTERS_INITIAL,
        sort: type === 'contest' ? 'startTime' : 'createdTimestamp'
      }
    }
  }
})

export const {
  // contest -- general
  setSelectedContest,

  // contest -- edit reducers
  setStageTableFilters,
  setCandidateTableFilters,
  setManualTableFilters,
  setLeaderboardTableFilters,

  // contest -- stage level reducers
  setStageLeaderboardTableFilters,
  setStagePendingEvaluationTableFilters,

  // resetters
  resetAllStageFilters,
  resetAllContestFilters,
  resetCandidateTableFilters,

  // contest dashboard exports
  setContestDashboardFilters,

  // Assessment Dashboard exports
  setSearchQuery,
  setSelectedStageId,
  setAssessmentDashboardTableFilters,

  // contest/assessment list filters exports
  setListFilters,
  resetListFilters
} = contestSlice.actions

export default contestSlice.reducer
